import React, { useCallback } from 'react';

import { Builder } from '@builder.io/react';
import PropTypes from 'prop-types';

import CtaNode from '~/shared/components/CtaNode';
import useTrackCtaButtonClick from '~/shared/utils/useTrackCtaButtonClick';

import { CtaVariantHumanToThemeMapping } from './BuilderCta.builder';

// For backward compatibility with snake_case variants
const snakeToCamelMap = {
  primary_dark: 'primaryDark',
  primary_light: 'primaryLight',
  primary_lavender: 'primaryLavender',
  secondary_dark: 'secondaryDark',
  secondary_light: 'secondaryLight',
  secondary_lavender: 'secondaryLavender',
  tertiary_dark: 'tertiaryDark',
  tertiary_light: 'tertiaryLight',
  // text_link is a special case that keeps its snake_case format in CtaNode
  text_link: 'text_link',
};

// Mapping to handle human-readable values
const humanReadableToThemeMap = CtaVariantHumanToThemeMapping;

/**
 * Normalizes variant to the format expected by the theme
 * @param {string} variantInput - Variant in any format (human-readable, snake_case, camelCase)
 * @returns {string} - Variant in the format expected by the theme
 */
const normalizeVariantForTheme = (variantInput) => {
  if (!variantInput) {
    return 'primary';
  }

  // Check if it's a human-readable label first
  if (humanReadableToThemeMap[variantInput]) {
    return humanReadableToThemeMap[variantInput];
  }

  // Check if it's a snake_case variant that needs conversion
  if (snakeToCamelMap[variantInput]) {
    return snakeToCamelMap[variantInput];
  }

  // If it's already a camelCase variant or anything else, return as is
  return variantInput;
};

const BuilderCta = ({
  attributes,
  text,
  link = '/',
  variant = 'primary',
  enableTracking = false,
  trackingData = {},
}) => {
  const dispatchTrackCtaButtonClick = useTrackCtaButtonClick();
  const isEditing = Builder.isEditing;

  // Normalize to the format expected by the theme
  const themeVariant = normalizeVariantForTheme(variant);

  const handleClick = useCallback(() => {
    if (!isEditing && enableTracking) {
      dispatchTrackCtaButtonClick({
        category: trackingData.category || '',
        label: text || '',
        title: trackingData.title || '',
      });
    }
  }, [
    isEditing,
    enableTracking,
    dispatchTrackCtaButtonClick,
    trackingData,
    text,
  ]);

  return (
    <CtaNode
      defaultMessage={text}
      targetUrl={link}
      variantStyle={themeVariant}
      onClick={handleClick}
      {...attributes}
      data-builder-component-type="cta"
    />
  );
};

BuilderCta.propTypes = {
  attributes: PropTypes.object,
  enableTracking: PropTypes.bool,
  link: PropTypes.string,
  text: PropTypes.string,
  trackingData: PropTypes.object,
  variant: PropTypes.string,
};

export default BuilderCta;
